import { useEffect } from "react"
import { Link } from "react-router-dom"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Search, PlusCircle, Eye } from "lucide-react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Pagination, PaginationContent, PaginationItem, PaginationNext, PaginationPrevious } from "@/components/ui/pagination"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import {
  getAllTickets,
  selectAdminTickets,
  selectAdminTicketsLoading,
  selectAdminTicketsPagination,
  selectTicketFilters,
  setStatusFilter,
  setSearchTerm,
  setAdminTicketsPage,
} from "@/store/slices/ticketSlice"
import Loader from "@/components/loader/loader"

export default function SupportTickets() {
  const dispatch = useAppDispatch()
  const tickets = useAppSelector(selectAdminTickets)
  const loading = useAppSelector(selectAdminTicketsLoading)
  const pagination = useAppSelector(selectAdminTicketsPagination)
  const filters = useAppSelector(selectTicketFilters)

  useEffect(() => {
    const fetchFilters = {
      status: filters.status || undefined,
      search: filters.searchTerm || undefined,
    }
    dispatch(getAllTickets({ page: pagination.page, limit: 10, filters: fetchFilters }))
  }, [dispatch, pagination.page, filters.status, filters.searchTerm])

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (pagination.page === 1) {
        const fetchFilters = {
          status: filters.status || undefined,
          search: filters.searchTerm || undefined,
        }
        dispatch(getAllTickets({ page: 1, limit: 10, filters: fetchFilters }))
      } else {
        dispatch(setAdminTicketsPage(1))
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [filters.searchTerm, filters.status, dispatch, pagination.page])

  const handleSearchChange = (value) => {
    dispatch(setSearchTerm(value))
  }

  const handleStatusFilterChange = (value) => {
    dispatch(setStatusFilter(value === "all" ? "" : value))
    dispatch(setAdminTicketsPage(1))
  }

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "Open":
        return "bg-green-100 text-green-700"
      case "Pending":
        return "bg-yellow-100 text-yellow-700"
      case "Closed":
        return "bg-gray-100 text-gray-700"
      case "Resolved":
        return "bg-blue-100 text-blue-700"
      case "Answered":
        return "bg-purple-100 text-purple-700"
      default:
        return "bg-gray-100 text-gray-700"
    }
  }

  const formatDateTime = (isoString) => {
    if (!isoString) return "N/A"
    const date = new Date(isoString)
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    })
  }

  const getCustomerName = (ticket) => {
    if (ticket.createdBy) {
      const firstName = ticket.createdBy.firstName || ""
      const lastName = ticket.createdBy.lastName || ""
      const fullName = `${firstName} ${lastName}`.trim()
      return fullName || ticket.createdBy.email || "Unknown"
    }
    return "Unknown"
  }

  return (
    <div className="grid gap-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-2xl font-bold">Support Tickets</CardTitle>
          <Button size="sm" asChild>
            <Link to="/admin/support/new">
              <PlusCircle className="h-4 w-4 mr-2" />
              New Ticket
            </Link>
          </Button>
        </CardHeader>
        <CardDescription className="px-6">Manage customer support inquiries.</CardDescription>
        <CardContent>
          <div className="flex items-center gap-4 py-4">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
              <Input
                type="search"
                placeholder="Search tickets by ID, subject, or customer..."
                className="w-full pl-8"
                value={filters.searchTerm || ""}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
            </div>
            <Select value={filters.status || "all"} onValueChange={handleStatusFilterChange}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="Open">Open</SelectItem>
                <SelectItem value="Pending">Pending</SelectItem>
                <SelectItem value="Answered">Answered</SelectItem>
                <SelectItem value="Resolved">Resolved</SelectItem>
                <SelectItem value="Closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[120px]">Ticket ID</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Update</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="h-24 text-center text-gray-500">
                      <Loader />
                    </TableCell>
                  </TableRow>
                ) : tickets.length > 0 ? (
                  tickets.map((ticket) => (
                    <TableRow key={ticket.ticket_id}>
                      <TableCell className="font-medium">{ticket.ticket_id}</TableCell>
                      <TableCell>{ticket.subject}</TableCell>
                      <TableCell>{getCustomerName(ticket)}</TableCell>
                      <TableCell>{ticket.category}</TableCell>
                      <TableCell>
                        <Badge className={getStatusBadgeColor(ticket.status)}>{ticket.status}</Badge>
                      </TableCell>
                      <TableCell>{formatDateTime(ticket.updated_at)}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="outline" size="sm" asChild>
                          <Link to={`/admin/support/${ticket.ticket_id}`}>
                            <Eye className="h-4 w-4 mr-2" /> View
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="h-24 text-center text-gray-500">
                      No tickets found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          {pagination.totalPages > 1 && (
            <Pagination className="mt-4">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={() => {
                      dispatch(setAdminTicketsPage(Math.max(1, pagination.page - 1)))
                    }}
                    aria-disabled={pagination.page === 1}
                    tabIndex={pagination.page === 1 ? -1 : undefined}
                    className={pagination.page === 1 ? "pointer-events-none opacity-50" : undefined}
                  />
                </PaginationItem>
                {Array.from({ length: pagination.totalPages }, (_, i) => (
                  <PaginationItem key={i}>
                    <Button
                      variant={pagination.page === i + 1 ? "default" : "outline"}
                      onClick={() => dispatch(setAdminTicketsPage(i + 1))}
                    >
                      {i + 1}
                    </Button>
                  </PaginationItem>
                ))}
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={() => {
                      dispatch(setAdminTicketsPage(Math.min(pagination.totalPages, pagination.page + 1)))
                    }}
                    aria-disabled={pagination.page === pagination.totalPages}
                    tabIndex={pagination.page === pagination.totalPages ? -1 : undefined}
                    className={pagination.page === pagination.totalPages ? "pointer-events-none opacity-50" : undefined}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
